<script lang="ts" setup>
import { computed, inject, ref } from 'vue';

import { i18n } from '@/i18n';
import SvgLoader from '@/utils/SvgLoader.vue';
import RowCheckBox from '@/components/atom/RowCheckBox.vue';
import Modal from '@/components/modal/Modal.vue';
import { DateService } from '@/coreDomainsModule/services/dateService';
import { IAnnounce } from '@/coreDomainsModule/entities/forecast';
import { SUPPLIER_FORECAST_LENGTH } from '@/coreDomainsModule/constants';
import MonthPicker from '@/corePagesModule/components/MonthPicker.vue';
import { IYearMonth } from '@/coreDomainsModule/date';
import { Ref } from 'vue';
import SimpleSelectInput from '@/corePagesModule/components/SimpleSelectInput/SimpleSelectInput.vue';
import { MAX_OF_DISPLAY_FORECAST_MONTH, MIN_OF_DISPLAY_FORECAST_MONTH, DEFAULT_DISPLAY_FORECAST_MONTH } from '../constants';
import TextButton from '@/components/input/Btn/TextButton.vue';
import { key } from './useRecommendOrderStore';

const props = defineProps<{
  calculatedAnnounceValues: IAnnounce[];
  announceHistories: IAnnounce[];
  receiveYearMonth?: IYearMonth;
  monthsRegisterSupplierForecast: number;
}>();

const emits = defineEmits<{
  (e: 'change-status', isChecked: boolean): void;
  (e: 'change-year-month', updateYearMonth: IYearMonth): void;
  (e: 'change-months-register-supplier-forecast', monthsRegisterSupplierForecast: number): void;
}>();

// style variables

//変数
const t = i18n.t;
const locale = computed(() => i18n.locale);
const isSameCustomerAnnounce = ref(true);

const modalState = ref(false);

const store = inject(key);

if (!store) {
  throw new Error('store is undefined');
}

// TODO: 年月選択の警告説明用
const todayForWarning = new Date();

// monthPicker表示用の年月情報
const receiveYearMonth: Ref<IYearMonth | undefined> = ref(props.receiveYearMonth);

const monthsRegisterSupplierForecast: Ref<number> = ref(props.monthsRegisterSupplierForecast);

/**
 * モーダルを開く
 */
const openModal = () => {
  if (!receiveYearMonth.value) return;
  modalState.value = true;
};
/**
 * モーダルを閉じる
 */
const closeModal = () => {
  modalState.value = false;
};

/**
 * 客先内示と同じ値を使用するかを変更する
 * @param isChecked - 客先内示と同じ値を使用するか
 */
const onChangeStatus = (isChecked: boolean) => {
  isSameCustomerAnnounce.value = isChecked;
  emits('change-status', isChecked);
};

/**
 * 内示の受領月を選択した際の処理
 * @param selectedYearMonth - 選択された年月
 */
const onSelectedReceiveMonth = async (selectedYearMonth: IYearMonth) => {
  if (selectedYearMonth.year === 0 || selectedYearMonth.month === 0) return;
  receiveYearMonth.value = selectedYearMonth;
  emits('change-year-month', selectedYearMonth);
};

/**
 * 内示の受領月を選択した際の処理
 * @param selectedMonthsRange - 選択された出力範囲
 */
const onSelectedMonthsRegisterSupplierForecast = async (selectedMonthsRange: number) => {
  const convertedSelectedMonthsRange = Number(selectedMonthsRange);
  if (convertedSelectedMonthsRange != null) {
    monthsRegisterSupplierForecast.value = convertedSelectedMonthsRange;
  } else {
    monthsRegisterSupplierForecast.value = DEFAULT_DISPLAY_FORECAST_MONTH - 1;
  }
  emits('change-months-register-supplier-forecast', convertedSelectedMonthsRange);
};

/**
 * 表示する内示の月数の選択肢
 */
const monthsOptionsDisplayForecast = Array(MAX_OF_DISPLAY_FORECAST_MONTH - MIN_OF_DISPLAY_FORECAST_MONTH + 1)
  .fill(0)
  .map((_, i) => `${i + MIN_OF_DISPLAY_FORECAST_MONTH}`);
</script>

<template>
  <div class="recommend-edit-supplier-announce">
    <div class="recommend-edit-supplier-announce__check-box">
      <div class="month-picker-content">
        <MonthPicker
          class="month-picker-content__calendar"
          :model-value="receiveYearMonth"
          :placeholder="t('年月を選択')"
          :locale="locale"
          :prop-format="t('MonthPickerFormat')"
          @update:model-value="onSelectedReceiveMonth($event)"
        />
      </div>
      <span>
        {{ '~' }}
      </span>
      <div class="select-register-month-range-content">
        <SimpleSelectInput
          class="select-register-month-range-content__select-box"
          :index="1"
          :model-value="`${monthsRegisterSupplierForecast}`"
          :custom-unit-value="'ヶ月分'"
          :select-box-name="'SELECT_REGISTER_MONTH_RANGE'"
          :prop-options="monthsOptionsDisplayForecast"
          :display-allow="false"
          @update:model-value="onSelectedMonthsRegisterSupplierForecast(Number($event))"
        />
      </div>
      <span>
        {{ t('までを保存(詳細ページ)') }}
      </span>
      <div class="same-customer-announce-container">
        <RowCheckBox
          class="same-customer-announce-container--check-box"
          :is-checked="isSameCustomerAnnounce"
          @change-status="onChangeStatus"
        />
        <i18n-t class="i18n-break" :keypath="'客先内示と同じ(詳細ページ)'" tag="span">
          <template #newLine>
            <br />
          </template>
        </i18n-t>
      </div>
      <!-- 客先内示記入モーダルを表示するボタン -->
      <div class="announce-table__modal-button-container" :class="{ 'disabled-div': !receiveYearMonth }">
        <span class="announce-table__modal-button-container--text" @click="openModal">
          {{ t('全ての月を表示(詳細ページ)') }}
        </span>
        <SvgLoader
          class="announce-table__show-all-announce--icon"
          :name="'doubleArrow'"
          data-testid="`announce-table__show-all-announce--icon`"
          :button-style="'btn-primary'"
        />
      </div>
    </div>

    <div class="announce-table-container">
      <table v-if="receiveYearMonth != null" class="announce-table-container__announce-table-area announce-table-area">
        <thead class="announce-table-area__table-head--container">
          <tr class="announce-table-area__table-head--row">
            <!-- 1列目 -->
            <th class="announce-table-area__table-head--column">
              {{ t('') }}
            </th>
            <!-- 2列目以降 -->
            <th
              v-for="(testObj, index) in calculatedAnnounceValues.slice(0, SUPPLIER_FORECAST_LENGTH)"
              :key="'header' + index"
              class="announce-table-area__table-head--column"
            >
              {{
                t('displayShortDate', {
                  year: t(`${testObj.year}`),
                  month: t(`${testObj.month}`),
                })
              }}
            </th>
          </tr>
        </thead>
        <tbody class="announce-table-area__table-body">
          <tr class="announce-table-area__table-body--row">
            <!-- シミュレーション行 -->
            <td class="announce-table-area__table-body--column">
              {{ t('仕入先内示(仕入先内示項目)') }}
            </td>
            <template v-for="(testObj, index) in calculatedAnnounceValues.slice(0, SUPPLIER_FORECAST_LENGTH)" :key="'contents' + index">
              <td class="announce-table-area__table-body--column">
                <span> {{ testObj.amount.addComma() }} </span>
              </td>
            </template>
          </tr>
          <tr class="announce-table-area__table-body--row">
            <!-- 受領内示行 -->
            <td class="announce-table-area__table-body--column">
              {{ t('前月内示(仕入先内示項目)') }}
            </td>
            <template v-for="(testObj, index) in announceHistories.slice(0, SUPPLIER_FORECAST_LENGTH)" :key="'default' + index">
              <td class="announce-table-area__table-body--column">
                <span> {{ testObj.amount.addComma() }} </span>
              </td>
            </template>
          </tr>
        </tbody>
      </table>
      <div v-else class="announce-table-area__not-selected-year-month">
        <span class="announce-table-area__not-selected-year-month--warning-text">
          {{ t('仕入先内示項目初期値1') }}
        </span>
        <span class="announce-table-area__not-selected-year-month--description-text">
          {{
            t('仕入先内示項目初期値2', {
              startMonth: t(`${todayForWarning.getMonth() + 1}月`),
              endMonth: t(
                `${DateService.getShiftedIYearMonthByMonths(todayForWarning.getFullYear(), todayForWarning.getMonth(), 3).month}月`
              ),
            })
          }}
        </span>
      </div>
    </div>

    <div v-if="store.state.progress === 'EditSupplierAnnounce'" class="recommend-edit-supplier-announce__button-area button-area">
      <TextButton class="button-area__revise-button" :button-style="'btn-secondary'" :tool-tip-value="''" :on-click="store.reviseRecommend">
        {{ t('もどる') }}
      </TextButton>
      <TextButton
        color="primary"
        :size="'btn-md'"
        :disabled="!receiveYearMonth"
        class="button-area__define-button"
        @click="store.confirmSupplierAnnounce(monthsRegisterSupplierForecast, isSameCustomerAnnounce, receiveYearMonth)"
      >
        {{ t('確定') }}
      </TextButton>
    </div>
  </div>

  <!-- 仕入先内示モーダル -->
  <Modal
    :stretch-container="true"
    modal-title="仕入先内示(詳細ページ)"
    border-radius="16px"
    :modal-state="modalState"
    :modal-size="'2xl'"
    @outside-click="closeModal"
  >
    <div class="announce-modal">
      <div class="announce-history-table-container">
        <table class="announce-history-table-container__announce-history-table-area announce-history-table-area">
          <thead class="announce-history-table-area__table-head--container">
            <tr class="announce-history-table-area__table-head--row">
              <!-- 1列目 -->
              <th class="announce-history-table-area__table-head--column">
                {{ t('') }}
              </th>
              <!-- 2列目以降 -->
              <th
                v-for="(testObj, index) in calculatedAnnounceValues"
                :key="'header' + index"
                class="announce-history-table-area__table-head--column"
              >
                {{
                  t('displayShortDate', {
                    year: t(`${testObj.year}`),
                    month: t(`${testObj.month}`),
                  })
                }}
              </th>
            </tr>
          </thead>
          <tbody class="announce-history-table-area__table-body">
            <tr class="announce-history-table-area__table-body--row">
              <!-- シミュレーション行 -->
              <td class="announce-history-table-area__table-body--column">
                {{ t('仕入先内示(仕入先内示項目)') }}
              </td>
              <template v-for="(testObj, index) in calculatedAnnounceValues" :key="'contents' + index">
                <td class="announce-history-table-area__table-body--column">
                  <span> {{ testObj.amount.addComma() }} </span>
                </td>
              </template>
            </tr>
            <tr class="announce-history-table-area__table-body--row">
              <!-- 受領内示行 -->
              <td class="announce-history-table-area__table-body--column">
                {{ t('前月内示(仕入先内示項目)') }}
              </td>
              <template v-for="(testObj, index) in announceHistories" :key="'default' + index">
                <td class="announce-history-table-area__table-body--column">
                  <span> {{ testObj.amount.addComma() }} </span>
                </td>
              </template>
            </tr>
          </tbody>
        </table>
      </div>
      <div class="announce-modal__close-button-container">
        <TextButton class="announce-modal__close-button" :button-style="'btn-cancel'" :size="'btn-md'" :on-click="closeModal">
          {{ t('閉じる') }}
        </TextButton>
      </div>
    </div>
  </Modal>
</template>

<style lang="scss" scoped>
.recommend-edit-supplier-announce {
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  grid-template-rows: auto auto minmax(0, 1fr) auto;
  row-gap: 8px;
  padding-top: 8px;
  &__title-container {
    display: grid;
    grid-template-columns: auto minmax(0, 1fr);
    align-items: center;
    &--title {
      font-family: Inter;
      font-size: $fontSizeM;
      color: $textDefault;
      line-height: 23px;
      font-weight: 700;
      justify-self: start;
    }
    &--showing-control-icon {
      margin-left: auto;
      padding-right: 8px;
    }
  }
  &__check-box {
    display: grid;
    align-items: center;
    grid-template-columns: minmax(96px, 0.3fr) fit-content(100%) fit-content(100%) fit-content(100%) minmax(108px, 0.7fr) auto;
    grid-template-rows: minmax(36px, 1fr);
    column-gap: 8px;
  }
  &__announce-table {
    margin-top: 12px;
    display: grid;
  }

  .same-customer-announce-container {
    display: grid;
    grid-template-columns: repeat(2, fit-content(100%));
    column-gap: 4px;
    place-items: center;
    height: 100%;
  }

  .month-picker-content {
    height: 100%;
    border-radius: 4px;
    border: solid 1px $borderDefault;

    &__calendar {
      display: grid;
      place-items: center;
      font-weight: 700;
      height: 100%;
    }
  }
  .select-register-month-range-content {
    height: 100%;
    width: 80px;
    border-radius: 4px;
    border: solid 1px $borderDefault;
    &__select-box {
      height: 100%;
    }
  }

  &__button-area {
    width: 100%;
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 8px;
    padding: 12px 24px;
  }
}

.button-area {
  &__revise-button {
    display: grid;
    grid-column: 2;
    align-content: center;
  }

  &__define-button {
    display: grid;
    grid-column: 3;
    align-content: center;
  }
}

.announce-table {
  &__modal-button-container {
    display: grid;
    grid-template-columns: repeat(2, fit-content(100%));
    align-self: end;
    grid-gap: 4px;
    margin-left: auto;
    padding-right: 4px;

    &--text {
      font-family: Inter;
      font-size: $fontSizeS;
      font-weight: 400;
      line-height: 18px;
      color: $semanticPrimary;
    }
    &:hover {
      cursor: v-bind('!receiveYearMonth ? `not-allowed` : `pointer`');
    }
  }

  &__show-all-announce--icon {
    align-self: center;
  }
}
.item-container {
  display: grid;
  align-items: center;
  justify-items: center;

  &__header-item {
    font-size: $fontSizeM;
    font-weight: bold;
    height: auto; // TODO: 仮置き
  }
  &__announce-item {
    font-size: $fontSizeM;
    color: $semanticPrimary;
    font-weight: bold;
    height: auto; // TODO: 仮置き
  }
  &__announce-history-item {
    font-size: $fontSizeM;
    color: $semanticPrimary;
    height: auto; // TODO: 仮置き
  }
}

.announce-table-container {
  width: 100%;
  height: 98px;
  overflow: hidden;
  border-radius: 4px;
  border: solid 1px $borderDefault;
  background-color: $surfaceDefault;

  .announce-table-area {
    width: 100%;
    table-layout: fixed;
    &__table-head {
      &--row {
        height: 30px;
        text-align: right;
      }
      &--column {
        min-width: 64px;
        &:last-child {
          padding-right: 8px;
        }
      }
      &--container {
        background-color: $surfaceDefault;
      }
    }
    &__table-body {
      background-color: $semanticWhite;
      &--row {
        height: 34px;
        text-align: right;
      }
      &--column {
        min-width: 64px;
        text-overflow: ellipsis;
        white-space: nowrap;
        overflow: hidden;
        max-width: 0;
        &:last-child {
          padding-right: 8px;
        }
      }
    }
    &__not-selected-year-month {
      display: grid;
      grid-template-rows: repeat(4, minmax(0, 1fr));
      grid-gap: 4px;
      align-items: center;
      background-color: $surfaceDefault;
      &--warning-text {
        grid-row: 2/2;
        text-align: center;
        color: $textSecondary;
        font-size: $fontSizeM;
        font-weight: 700;
      }
      &--description-text {
        width: 100%;
        grid-row: 3/3;
        text-align: center;
        color: $textSecondary;
        font-size: $fontSizeS;
      }
    }
  }
}

.announce-history-table-container {
  width: 100%;
  height: 98px;
  overflow: hidden;
  overflow-x: scroll;
  border-radius: 4px;
  border: solid 1px $borderDefault;

  .announce-history-table-area {
    table-layout: fixed;
    &__table-head {
      &--row {
        height: 30px;
        text-align: right;
      }
      &--column {
        min-width: 64px;
        &:first-child {
          min-width: 144px;
          padding-right: 8px;
          position: sticky;
          background-color: $surfaceDefault;
          left: 0;
        }
        &:last-child {
          padding-right: 8px;
        }
      }
      &--container {
        background-color: $surfaceDefault;
      }
    }
    &__table-body {
      background-color: $semanticWhite;
      &--row {
        height: 34px;
        text-align: right;
      }
      &--column {
        min-width: 64px;
        text-overflow: ellipsis;
        white-space: nowrap;
        overflow: hidden;
        max-width: 0;
        &:first-child {
          min-width: 144px;
          padding-right: 8px;
          position: sticky;
          background-color: $semanticWhite;
          left: 0;
        }
        &:last-child {
          padding-right: 8px;
        }
      }
    }
  }
}
.announce-modal {
  display: grid;
  grid-template-rows: repeat(2, fit-content(100%));
  grid-gap: 16px;
  align-items: center;
  background-color: $semanticWhite;
  &__close-button-container {
    margin-right: auto;
    margin-left: auto;
  }
}

// divにdisabledを付与してるように見せる
.disabled-div {
  cursor: v-bind('!receiveYearMonth ? `not-allowed` : `pointer`');
  opacity: 0.5;
}
</style>
